import { Link, useLocation } from "wouter";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useCloseMobileSidebarOnNavigate } from "@/hooks/use-close-mobile-sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Shield,
  Settings,
  Activity,
  LogOut,
  Home,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { adminOperationsNavItems } from "@/lib/platform-nav";
import { SidebarBrand } from "@/components/public/logo-mark";

interface NotificationCounts {
  tickets: number;
  announcements: number;
}

const superadminNavItems = [
  { icon: LayoutDashboard, label: "Dashboard", href: "/superadmin" },
  { icon: Shield, label: "Admin Users", href: "/superadmin/admins" },
  { icon: Activity, label: "Activity Logs", href: "/superadmin/activity" },
  { icon: Settings, label: "System Settings", href: "/superadmin/settings" },
];

export function SuperAdminSidebar() {
  const [location] = useLocation();
  const { user, logout } = useAuth();
  useCloseMobileSidebarOnNavigate();

  const { data: counts } = useQuery<NotificationCounts>({
    queryKey: ["/api/admin/notifications/counts"],
    refetchInterval: 30000,
  });

  const initials = user
    ? `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || user.email?.[0]?.toUpperCase() || "SA"
    : "SA";

  return (
    <Sidebar className="border-r">
      <SidebarHeader className="p-4">
        <SidebarBrand panelLabel="Super Admin Panel" />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>System Management</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {superadminNavItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.href}
                    data-testid={`superadmin-sidebar-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Link href={item.href}>
                      <item.icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Operations (Admin)</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {adminOperationsNavItems.map((item) => {
                const count = item.badge && counts ? (counts as Record<string, number>)[item.badge] : 0;
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={location === item.href}
                      data-testid={`superadmin-ops-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Link href={item.href} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <item.icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </div>
                        {count > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
                            {count > 99 ? "99+" : count}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4 border-t">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.profileImageUrl || undefined} alt={user?.firstName || "SA"} />
              <AvatarFallback className="bg-accent text-primary text-sm font-medium">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium truncate">
                {user?.firstName || "Super"} {user?.lastName || "Admin"}
              </span>
              <span className="text-xs text-muted-foreground truncate">
                {user?.email || "superadmin@godmancapital.in"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Link href="/" className="flex-1">
            <Button variant="outline" size="sm" className="w-full gap-2" data-testid="superadmin-button-home">
              <Home className="w-4 h-4" />
              Home
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={() => logout()}
            className="flex-1 gap-2"
            data-testid="superadmin-button-logout"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
